/**
 * Šprtec 3D — vykreslování scény (Three.js).
 * Stará se o stůl, kameny, puk, mířicí pomůcky, kameru a efekty.
 * Herní logika sem nepatří — scéna jen zobrazuje stav těles.
 */

import * as THREE from 'three';
import {
  RINK, SEGMENTS, CORNERS, PIECE_R, PUCK_R, PUCK_ID, makeBodies, castDisc,
} from '../shared/physics.js';

const L2 = RINK.L / 2;
const W2 = RINK.W / 2;
const GW2 = RINK.GW / 2;
const { CR, GD } = RINK;

const BOARD_H = 0.11;
const BOARD_TH = 0.04;
const TABLE_W = RINK.W + 0.52; // celková deska stolu
const TABLE_L = RINK.L + 1.0;

export const TEAM_COLORS = [0xd93a26, 0x2f6fe0];
const TEAM_COLORS_DARK = [0x8f2114, 0x1c4494];

export class Scene3D {
  constructor(canvas) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0d1015);
    this.scene.fog = new THREE.Fog(0x0d1015, 7, 14);

    this.camera = new THREE.PerspectiveCamera(48, 1, 0.1, 40);
    this.camDesired = { pos: new THREE.Vector3(0, 2.4, -3.1), tgt: new THREE.Vector3(0, 0, 0) };
    this.camTarget = new THREE.Vector3(0, 0, 0);
    this.camera.position.copy(this.camDesired.pos);
    this.camSide = 0;
    this.camMode = 'side';
    this.camSnap = true;

    this.raycaster = new THREE.Raycaster();
    this.tablePlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.pointerVec = new THREE.Vector2();

    this.meshes = new Map(); // id -> { mesh, tx, tz }
    this.teleportNext = true;
    this.goalLights = [];
    this.ringPulse = 0;

    this.buildLights();
    this.buildTable();
    this.buildBodies();
    this.buildAimHelpers();

    window.addEventListener('resize', () => this.resize());
    this.resize();
  }

  // ------------------------------------------------------------- osvětlení

  buildLights() {
    this.scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x2a2018, 0.75));

    const key = new THREE.DirectionalLight(0xfff1dc, 2.4);
    key.position.set(1.8, 3.6, 1.1);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -2.2;
    key.shadow.camera.right = 2.2;
    key.shadow.camera.top = 2.6;
    key.shadow.camera.bottom = -2.6;
    key.shadow.camera.far = 10;
    key.shadow.bias = -0.0004;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xbfd4ff, 0.5);
    fill.position.set(-2.2, 2.4, -1.6);
    this.scene.add(fill);

    // světla nad brankami — bliknou při gólu
    for (const zSign of [-1, 1]) {
      const l = new THREE.PointLight(0xff3222, 0, 3, 1.6);
      l.position.set(0, 0.7, zSign * (L2 + 0.1));
      this.scene.add(l);
      this.goalLights.push(l);
    }
  }

  // ------------------------------------------------------------------ stůl

  buildTable() {
    // deska stolu s natištěným kluzištěm
    const topTex = this.makeIceTexture();
    const top = new THREE.Mesh(
      new THREE.PlaneGeometry(TABLE_W, TABLE_L),
      new THREE.MeshStandardMaterial({ map: topTex, roughness: 0.36, metalness: 0.05 }),
    );
    top.rotation.x = -Math.PI / 2;
    top.receiveShadow = true;
    this.scene.add(top);

    // korpus stolu + nohy + podlaha
    const woodDark = new THREE.MeshStandardMaterial({ color: 0x4a2f1b, roughness: 0.7 });
    const body = new THREE.Mesh(new THREE.BoxGeometry(TABLE_W, 0.09, TABLE_L), woodDark);
    body.position.y = -0.048;
    this.scene.add(body);

    const legGeo = new THREE.BoxGeometry(0.09, 0.85, 0.09);
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const leg = new THREE.Mesh(legGeo, woodDark);
      leg.position.set(sx * (TABLE_W / 2 - 0.12), -0.52, sz * (TABLE_L / 2 - 0.14));
      this.scene.add(leg);
    }

    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(9, 48),
      new THREE.MeshStandardMaterial({ color: 0x15181e, roughness: 0.95 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.95;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // mantinely přesně podle fyzikálních segmentů
    const boardMat = new THREE.MeshStandardMaterial({ color: 0xf2f3f5, roughness: 0.45 });
    for (const [x1, z1, x2, z2, nx, nz] of SEGMENTS) {
      const len = Math.hypot(x2 - x1, z2 - z1);
      const geo = nx !== 0
        ? new THREE.BoxGeometry(BOARD_TH, BOARD_H, len)
        : new THREE.BoxGeometry(len, BOARD_H, BOARD_TH);
      const wall = new THREE.Mesh(geo, boardMat);
      wall.position.set(
        (x1 + x2) / 2 + nx * (BOARD_TH / 2),
        BOARD_H / 2,
        (z1 + z2) / 2 + nz * (BOARD_TH / 2),
      );
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.scene.add(wall);
    }

    // zaoblené rohy mantinelů (čtvrtprstenec vytažený do výšky)
    for (const [cx, cz] of CORNERS) {
      const a0 = Math.atan2(Math.sign(cz), 0);            // směr ke koncové stěně
      const shape = new THREE.Shape();
      const start = Math.sign(cx) > 0 ? 0 : Math.PI;      // směr k bočnici
      shape.absarc(0, 0, CR + BOARD_TH, start, a0, (start === 0) !== (Math.sign(cz) > 0));
      shape.absarc(0, 0, CR, a0, start, (start === 0) === (Math.sign(cz) > 0));
      const geo = new THREE.ExtrudeGeometry(shape, { depth: BOARD_H, bevelEnabled: false });
      geo.rotateX(Math.PI / 2);
      geo.translate(0, BOARD_H, 0);
      const corner = new THREE.Mesh(geo, boardMat);
      corner.position.set(cx, 0, cz);
      corner.castShadow = true;
      this.scene.add(corner);
    }

    this.buildGoals();
  }

  buildGoals() {
    const frameMat = new THREE.MeshStandardMaterial({ color: 0xd32f2f, roughness: 0.35, metalness: 0.35 });
    const netTex = this.makeNetTexture();
    const netMat = new THREE.MeshBasicMaterial({
      map: netTex, transparent: true, side: THREE.DoubleSide, depthWrite: false, opacity: 0.9,
    });
    const H = 0.13;

    for (const zSign of [-1, 1]) {
      const g = new THREE.Group();

      const postGeo = new THREE.CylinderGeometry(0.011, 0.011, H, 10);
      for (const xSign of [-1, 1]) {
        const post = new THREE.Mesh(postGeo, frameMat);
        post.position.set(xSign * GW2, H / 2, 0);
        post.castShadow = true;
        g.add(post);
      }
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.011, 0.011, GW2 * 2, 10), frameMat);
      bar.rotation.z = Math.PI / 2;
      bar.position.set(0, H, 0);
      bar.castShadow = true;
      g.add(bar);

      // síť: zadní stěna, boky a „střecha“
      const back = new THREE.Mesh(new THREE.PlaneGeometry(GW2 * 2, H), netMat);
      back.position.set(0, H / 2, zSign * GD);
      g.add(back);
      for (const xSign of [-1, 1]) {
        const sideNet = new THREE.Mesh(new THREE.PlaneGeometry(GD, H), netMat);
        sideNet.rotation.y = Math.PI / 2;
        sideNet.position.set(xSign * GW2, H / 2, zSign * GD / 2);
        g.add(sideNet);
      }
      const roof = new THREE.Mesh(new THREE.PlaneGeometry(GW2 * 2, GD), netMat);
      roof.rotation.x = Math.PI / 2;
      roof.position.set(0, H, zSign * GD / 2);
      g.add(roof);

      g.position.set(0, 0, zSign * L2);
      this.scene.add(g);
    }
  }

  /** Textura desky: dřevo okolo, uvnitř bílý led s hokejovým značením. */
  makeIceTexture() {
    const PX = 360; // pixelů na metr
    const cw = Math.round(TABLE_W * PX);
    const ch = Math.round(TABLE_L * PX);
    const c = document.createElement('canvas');
    c.width = cw;
    c.height = ch;
    const ctx = c.getContext('2d');
    const X = (x) => (x + TABLE_W / 2) * PX;
    const Y = (z) => (z + TABLE_L / 2) * PX;
    const M = (m) => m * PX;

    // dřevo s jemnými léty
    ctx.fillStyle = '#7a4f28';
    ctx.fillRect(0, 0, cw, ch);
    for (let i = 0; i < 60; i++) {
      ctx.strokeStyle = `rgba(0,0,0,${0.03 + Math.random() * 0.05})`;
      ctx.lineWidth = 1 + Math.random() * 3;
      const y = Math.random() * ch;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(cw * 0.3, y + 14, cw * 0.6, y - 14, cw, y + 6);
      ctx.stroke();
    }

    // obrys kluziště včetně brankovišť
    const rinkPath = () => {
      ctx.beginPath();
      ctx.moveTo(X(-W2 + CR), Y(-L2));
      ctx.lineTo(X(W2 - CR), Y(-L2));
      ctx.arcTo(X(W2), Y(-L2), X(W2), Y(-L2 + CR), M(CR));
      ctx.lineTo(X(W2), Y(L2 - CR));
      ctx.arcTo(X(W2), Y(L2), X(W2 - CR), Y(L2), M(CR));
      ctx.lineTo(X(-W2 + CR), Y(L2));
      ctx.arcTo(X(-W2), Y(L2), X(-W2), Y(L2 - CR), M(CR));
      ctx.lineTo(X(-W2), Y(-L2 + CR));
      ctx.arcTo(X(-W2), Y(-L2), X(-W2 + CR), Y(-L2), M(CR));
      ctx.closePath();
    };

    // cesta kluziště + obou brankovišť (pro výplň i ořez značení)
    const rinkWithGoals = () => {
      rinkPath();
      ctx.rect(X(-GW2), Y(-L2 - GD), M(GW2 * 2), M(GD));
      ctx.rect(X(-GW2), Y(L2), M(GW2 * 2), M(GD));
    };

    // led
    rinkWithGoals();
    const grad = ctx.createLinearGradient(0, Y(-L2), 0, Y(L2));
    grad.addColorStop(0, '#f4f8fb');
    grad.addColorStop(0.5, '#ffffff');
    grad.addColorStop(1, '#f2f6fa');
    ctx.fillStyle = grad;
    ctx.fill();

    // od teď kreslíme jen uvnitř ledové plochy
    ctx.save();
    rinkWithGoals();
    ctx.clip();

    // brankoviště (za brankovou čárou)
    for (const zs of [-1, 1]) {
      ctx.fillStyle = '#eef3f8';
      ctx.fillRect(X(-GW2), Math.min(Y(zs * L2), Y(zs * (L2 + GD))), M(GW2 * 2), M(GD));
    }

    const line = (z, color, width) => {
      ctx.fillStyle = color;
      ctx.fillRect(X(-W2), Y(z) - width / 2, M(W2 * 2), width);
    };

    // střední a modré čáry
    line(0, '#d23b2e', M(0.03));
    line(-0.5, '#2456b0', M(0.025));
    line(0.5, '#2456b0', M(0.025));

    // brankové čáry
    for (const zs of [-1, 1]) {
      ctx.fillStyle = '#d23b2e';
      ctx.fillRect(X(-W2 + 0.04), Y(zs * L2) - M(0.008), M(W2 * 2 - 0.08), M(0.016));
    }

    // brankoviště — modrý půlkruh
    for (const zs of [-1, 1]) {
      ctx.fillStyle = 'rgba(80, 140, 220, 0.35)';
      ctx.beginPath();
      ctx.arc(X(0), Y(zs * L2), M(0.2), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#d23b2e';
      ctx.lineWidth = M(0.012);
      ctx.stroke();
    }

    // středový kruh + bod
    ctx.strokeStyle = '#2456b0';
    ctx.lineWidth = M(0.02);
    ctx.beginPath();
    ctx.arc(X(0), Y(0), M(0.26), 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = '#2456b0';
    ctx.beginPath();
    ctx.arc(X(0), Y(0), M(0.03), 0, Math.PI * 2);
    ctx.fill();

    // kruhy na vhazování
    for (const zs of [-1, 1]) {
      for (const xs of [-1, 1]) {
        ctx.strokeStyle = '#d23b2e';
        ctx.lineWidth = M(0.015);
        ctx.beginPath();
        ctx.arc(X(xs * 0.5), Y(zs * 0.93), M(0.18), 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#d23b2e';
        ctx.beginPath();
        ctx.arc(X(xs * 0.5), Y(zs * 0.93), M(0.022), 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // nápis ve středovém kruhu
    ctx.save();
    ctx.translate(X(0), Y(0));
    ctx.rotate(-Math.PI / 2);
    ctx.font = `700 ${M(0.09)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(36, 86, 176, 0.18)';
    ctx.fillText('ŠPRTEC 3D', 0, 0);
    ctx.restore();

    ctx.restore(); // konec ořezu na kluziště

    // červený lem kluziště
    rinkPath();
    ctx.strokeStyle = '#b23422';
    ctx.lineWidth = M(0.015);
    ctx.stroke();

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    return tex;
  }

  makeNetTexture() {
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 128;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, 128, 128);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    for (let i = 0; i <= 128; i += 12) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, 128); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(128, i); ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(3, 1);
    return tex;
  }

  // ---------------------------------------------------------------- tělesa

  /** Textura horní plochy kamene: barva týmu, bílý prstenec, číslo. */
  makePieceTopTexture(side, number) {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const ctx = c.getContext('2d');
    const color = side === 0 ? '#d93a26' : '#2f6fe0';
    const dark = side === 0 ? '#8f2114' : '#1c4494';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 128, 128);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.arc(64, 64, 50, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = number === 1 ? '#ffd23f' : '#ffffff';
    ctx.font = '900 56px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = dark;
    ctx.lineWidth = 3;
    const label = String(number);
    ctx.fillText(label, 64, 68);
    ctx.strokeText(label, 64, 68);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  buildBodies() {
    const H = 0.028;
    for (const b of makeBodies()) {
      let mesh;
      if (b.kind === 'puck') {
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(PUCK_R, PUCK_R, 0.02, 28),
          new THREE.MeshStandardMaterial({ color: 0x15161a, roughness: 0.35, metalness: 0.15 }),
        );
        mesh.position.y = 0.01;
      } else {
        const number = (b.id % 5) + 1;
        const sideMat = new THREE.MeshStandardMaterial({
          color: TEAM_COLORS_DARK[b.side], roughness: 0.32, metalness: 0.2,
        });
        const topMat = new THREE.MeshStandardMaterial({
          map: this.makePieceTopTexture(b.side, number), roughness: 0.28, metalness: 0.1,
        });
        const botMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.8 });
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(PIECE_R, PIECE_R * 1.06, H, 36),
          [sideMat, topMat, botMat],
        );
        mesh.position.y = H / 2;

        // prsten zvýraznění tahu
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(PIECE_R * 1.18, PIECE_R * 1.42, 32),
          new THREE.MeshBasicMaterial({
            color: TEAM_COLORS[b.side], transparent: true, opacity: 0, side: THREE.DoubleSide,
          }),
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.y = -H / 2 + 0.004;
        mesh.add(ring);
        mesh.userData.ring = ring;
      }
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.position.x = b.x;
      mesh.position.z = b.z;
      mesh.userData.side = b.side;
      mesh.userData.kind = b.kind;
      this.scene.add(mesh);
      this.meshes.set(b.id, { mesh, tx: b.x, tz: b.z });
    }
  }

  /** Okamžité nastavení pozic (lokální hra — fyzika běží v prohlížeči). */
  setBodiesDirect(bodies) {
    for (const b of bodies) {
      const m = this.meshes.get(b.id);
      if (!m) continue;
      m.tx = b.x;
      m.tz = b.z;
      m.mesh.position.x = b.x;
      m.mesh.position.z = b.z;
    }
  }

  /** Cílové pozice ze síťového snapshotu — vyhlazuje se v update(). */
  setBodiesTargets(snapBodies) {
    for (const [id, x, z] of snapBodies) {
      const m = this.meshes.get(id);
      if (!m) continue;
      m.tx = x;
      m.tz = z;
      if (this.teleportNext) {
        m.mesh.position.x = x;
        m.mesh.position.z = z;
      }
    }
    this.teleportNext = false;
  }

  /** Prstence pod kameny strany, která je na tahu (null = nic). */
  setHighlights(side) {
    for (const { mesh } of this.meshes.values()) {
      const ring = mesh.userData.ring;
      if (ring) ring.userData.on = mesh.userData.side === side;
    }
  }

  // ------------------------------------------------------------- míření

  buildAimHelpers() {
    this.aimGroup = new THREE.Group();
    this.aimGroup.visible = false;

    const shaft = new THREE.Mesh(
      new THREE.BoxGeometry(0.022, 0.006, 1),
      new THREE.MeshBasicMaterial({ color: 0x38c172 }),
    );
    shaft.position.z = 0.5;
    const head = new THREE.Mesh(
      new THREE.ConeGeometry(0.045, 0.09, 12),
      new THREE.MeshBasicMaterial({ color: 0x38c172 }),
    );
    head.rotation.x = Math.PI / 2;
    head.position.z = 1.04;
    this.aimShaft = shaft;
    this.aimHead = head;
    this.aimGroup.add(shaft, head);
    this.aimGroup.position.y = 0.035;
    this.scene.add(this.aimGroup);

    // tečkovaná vodicí linka + duch kamene v místě prvního kontaktu
    this.guideGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(), new THREE.Vector3(0, 0, 1),
    ]);
    this.guideLine = new THREE.Line(
      this.guideGeo,
      new THREE.LineDashedMaterial({
        color: 0xffffff, transparent: true, opacity: 0.55, dashSize: 0.05, gapSize: 0.04,
      }),
    );
    this.guideLine.visible = false;
    this.scene.add(this.guideLine);

    this.ghost = new THREE.Mesh(
      new THREE.RingGeometry(PIECE_R * 0.85, PIECE_R, 28),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
    );
    this.ghost.rotation.x = -Math.PI / 2;
    this.ghost.position.y = 0.005;
    this.ghost.visible = false;
    this.scene.add(this.ghost);
  }

  /**
   * Ukáže mířidla: šipka od kamene ve směru střely, vodicí linka
   * po první kontakt (spočtený nad aktuálními tělesy).
   */
  showAim(piece, dir, power, bodies) {
    const len = 0.25 + power * 0.55;
    this.aimGroup.visible = true;
    this.aimGroup.position.x = piece.x;
    this.aimGroup.position.z = piece.z;
    this.aimGroup.rotation.y = Math.atan2(dir.x, dir.z);
    this.aimShaft.scale.z = len;
    this.aimShaft.position.z = len / 2;
    this.aimHead.position.z = len + 0.04;

    const color = new THREE.Color().setHSL(0.36 * (1 - power), 0.85, 0.5);
    this.aimShaft.material.color = color;
    this.aimHead.material.color = color;

    const cast = castDisc(piece.x, piece.z, dir.x, dir.z, PIECE_R, bodies, [piece.id], 3.5);
    const end = new THREE.Vector3(
      piece.x + dir.x * cast.dist,
      0.03,
      piece.z + dir.z * cast.dist,
    );
    this.guideGeo.setFromPoints([new THREE.Vector3(piece.x, 0.03, piece.z), end]);
    this.guideLine.computeLineDistances();
    this.guideLine.visible = true;
    this.ghost.visible = true;
    this.ghost.position.x = end.x;
    this.ghost.position.z = end.z;
  }

  hideAim() {
    this.aimGroup.visible = false;
    this.guideLine.visible = false;
    this.ghost.visible = false;
  }

  // ------------------------------------------------------------- interakce

  /** Průsečík paprsku pod kurzorem s hrací plochou (y = 0). */
  pickTablePoint(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerVec.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointerVec, this.camera);
    const out = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.tablePlane, out)
      ? { x: out.x, z: out.z }
      : null;
  }

  // --------------------------------------------------------------- kamera

  setCamera(side, mode, snap = false) {
    this.camSide = side;
    this.camMode = mode;
    if (snap) this.camSnap = true;
    this.applyCameraPreset();
  }

  applyCameraPreset() {
    const portrait = window.innerHeight > window.innerWidth;
    const s = this.camSide === 0 ? -1 : 1;
    if (this.camMode === 'top') {
      this.camDesired.pos.set(0, portrait ? 3.35 : 3.6, s * 0.55);
      this.camDesired.tgt.set(0, 0, 0);
      this.camera.fov = portrait ? 66 : 50;
    } else {
      this.camDesired.pos.set(0, portrait ? 3.0 : 2.35, s * (portrait ? 3.1 : 3.05));
      this.camDesired.tgt.set(0, 0, s * 0.1);
      this.camera.fov = portrait ? 62 : 48;
    }
    this.camera.updateProjectionMatrix();
  }

  // --------------------------------------------------------------- efekty

  goalPulse(scorerSide) {
    // gól padl do branky, na kterou útočí scorerSide
    const idx = scorerSide === 0 ? 1 : 0; // branka u +z má index 1
    this.goalLights[idx].intensity = 10;
  }

  // ----------------------------------------------------------------- smyčka

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.applyCameraPreset();
  }

  update(dt) {
    // vyhlazení síťových pozic
    const k = 1 - Math.exp(-16 * dt);
    for (const { mesh, tx, tz } of this.meshes.values()) {
      mesh.position.x += (tx - mesh.position.x) * k;
      mesh.position.z += (tz - mesh.position.z) * k;
    }

    // pulz prstenců
    this.ringPulse += dt * 3;
    const pulse = 0.4 + 0.25 * Math.sin(this.ringPulse);
    for (const { mesh } of this.meshes.values()) {
      const ring = mesh.userData.ring;
      if (!ring) continue;
      const want = ring.userData.on ? pulse : 0;
      ring.material.opacity += (want - ring.material.opacity) * Math.min(1, dt * 8);
    }

    // doznívání gólových světel
    for (const l of this.goalLights) {
      if (l.intensity > 0.01) l.intensity *= Math.exp(-2.2 * dt);
      else l.intensity = 0;
    }

    // kamera
    const ck = this.camSnap ? 1 : 1 - Math.exp(-4.5 * dt);
    this.camSnap = false;
    this.camera.position.lerp(this.camDesired.pos, ck);
    this.camTarget.lerp(this.camDesired.tgt, ck);
    this.camera.lookAt(this.camTarget);

    this.renderer.render(this.scene, this.camera);
  }
}
